import {useMemo, useState} from "react";

import {capitalize} from "@helpers";
import {useTranslation} from "react-i18next";

export const useLevelOrder = () => {
  const {t} = useTranslation();

  const SCENARIO_LEVELS = useMemo(
    () => [
      {
        id: 0,
        value: "easy",
        type: "scenario",
        remainingTime: 1200000, // 20 min
        subtitle: t("popup.simulation.easy_scenario_subtitle"),
        title: t("popup.simulation.scenario_title"),
        text: t("popup.simulation.easy_scenario_text"),
      },
      {
        id: 1,
        value: "medium",
        type: "scenario",
        remainingTime: 960000, // 16 min
        subtitle: t("popup.simulation.medium_scenario_subtitle"),
        title: t("popup.simulation.scenario_title"),
        text: t("popup.simulation.medium_scenario_text"),
      },
      {
        id: 2,
        value: "hard",
        type: "scenario",
        remainingTime: 600000, // 10 min
        subtitle: t("popup.simulation.hard_scenario_subtitle"),
        title: t("popup.simulation.scenario_title"),
        text: t("popup.simulation.hard_scenario_text"),
      },
    ],
    [t],
  );

  const PRODUCT_LEVELS = useMemo(
    () => [
      {
        id: 0,
        type: "product",
        value: "easy",
        remainingTime: 840000, // 14 min
        question_count: 5,
        subtitle: t("popup.simulation.easy_product_subtitle"),
        title: t("popup.simulation.product_title"),
        text: t("popup.simulation.easy_product_text"),
      },
      {
        id: 1,
        type: "product",
        value: "medium",
        remainingTime: 1080000, // 18 min
        question_count: 7,
        subtitle: t("popup.simulation.medium_product_subtitle"),
        title: t("popup.simulation.product_title"),
        text: t("popup.simulation.medium_product_text"),
      },
      {
        id: 2,
        type: "product",
        value: "hard",
        remainingTime: 1200000, // 20 min
        question_count: 9,
        subtitle: t("popup.simulation.hard_product_subtitle"),
        title: t("popup.simulation.product_title"),
        text: t("popup.simulation.hard_product_text"),
      },
    ],
    [t],
  );

  const CERTIFICATION_LEVELS = useMemo(
    () => [
      {
        id: 1,
        type: "certification",
        value: "exam",
        remainingTime: 600000, // 10 min
        subtitle: t("popup.simulation.grand_certification_subtitle"),
        title: t("popup.simulation.certification_title"),
        text: t("popup.simulation.grand_certification_text"),
      },
    ],
    [t],
  );

  const scenarioLevels = useMemo(() => {
    return SCENARIO_LEVELS.map((level) => ({
      ...level,
      name: t(`level.${level.value}`),
    }));
  }, [t]);

  const productLevels = useMemo(() => {
    return PRODUCT_LEVELS.map((level) => ({
      ...level,
      name: t(`level.${level.value}`),
    }));
  }, [t]);

  const certificationLevels = useMemo(() => {
    return CERTIFICATION_LEVELS.map((level) => ({
      ...level,
      name: t(`level.${level.value}`),
    }));
  }, [t]);

  const currentLevel = (level) => {
    switch (level) {
      case "easy":
        return capitalize(t("level.easy"));
      case "medium":
        return capitalize(t("level.medium"));
      case "hard":
        return capitalize(t("level.hard"));
      case "practice":
        return capitalize(t("level.practice"));
      case "exam":
        return capitalize(t("level.exam"));
      default:
        return "";
    }
  };

  const currentLevelStyle = (level) => {
    switch (level) {
      case "easy":
        return {background: "var(--blueLight)", color: "var(--blue)"};
      case "medium":
        return {background: "var(--yellow)", color: "var(--yellowDark)"};
      case "hard":
        return {background: "var(--red)", color: "var(--redDark)"};
      case "practice":
        return {background: "var(--greyLight)", color: "var(--grey)"};
      case "exam":
        return {background: "var(--violetLight)", color: "var(--violet)"};
      default:
        return {background: "var(--black)", color: "var(--white)"};
    }
  };

  const [currentScenarioLevels] = useState(scenarioLevels);
  const [currentProductLevels] = useState(productLevels);
  const [currentCertificationLevels] = useState(certificationLevels);

  return {
    currentLevel,
    currentLevelStyle,
    certificationLevels: currentCertificationLevels,
    scenarioLevels: currentScenarioLevels,
    productLevels: currentProductLevels,
  };
};
